import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "risa-oasis-ui_v2";
import { getAuthConfig } from "../config/AuthConfigContext";
import { useAuthDispatch } from "../state/AuthStoreProvider";
import { logOutUserFromFirebase } from "../state/thunks/logOutThunk";
import OutlineWarningIcon from "../svg/outline-warning-icon";

/**
 * Props for the authentication error full-page view.
 */
export interface AuthErrorPageProps {
  /** Primary heading shown to the user. */
  title?: string;
  /** Supporting description with next steps. */
  message?: string;
  /** Whether to show a page reload action alongside sign out. */
  showRefreshButton?: boolean;
  /** Settings route used to recover from an inaccessible selected facility. */
  settingsRecoveryPath?: string;
}

/**
 * Render the package-owned icon for facility recovery settings.
 *
 * @returns Accessible decorative settings icon
 */
function SettingsRecoveryIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1A1.7 1.7 0 0 0 9 4.6 1.7 1.7 0 0 0 10 3v-.2h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z" />
    </svg>
  );
}

/**
 * Full-page state shown when the user cannot access the app (RBAC failure,
 * missing facility, or similar). Matches dashboard typography and actions.
 *
 * @param props - Optional title, message, and refresh button visibility.
 * @returns Centered error layout with sign-out (and optional refresh).
 */
const AuthErrorPage = ({
  title = "No Healthcare Facility Assigned",
  message = "Your account is not currently associated with any healthcare facility. Please contact your administrator to get access.",
  showRefreshButton = true,
  settingsRecoveryPath,
}: AuthErrorPageProps) => {
  const dispatch = useAuthDispatch();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await dispatch(logOutUserFromFirebase());
    const config = getAuthConfig();
    navigate(config?.authRedirectPath ?? "/auth", { replace: true });
    window.location.reload();
  };

  return (
    <div className="flex h-screen min-h-0 w-full">
      {settingsRecoveryPath && (
        <div className="group relative z-50 h-screen w-2 flex-none transition-[width] duration-200 ease-out hover:w-20 focus-within:w-20">
          <aside className="absolute inset-y-0 left-0 flex h-full w-20 -translate-x-full flex-col overflow-hidden bg-primaryGray-1 text-white shadow-lg transition-transform duration-200 ease-out group-hover:translate-x-0 group-focus-within:translate-x-0">
            <div className="flex h-20 items-center justify-center border-b border-white/10 text-xl font-bold">
              RISA
            </div>
            <nav className="flex flex-1 flex-col justify-end border-t border-white/10">
              <button
                type="button"
                onClick={() => navigate(settingsRecoveryPath)}
                title="Settings"
                aria-label="Open settings to switch organization"
                className="flex w-full items-center justify-center py-5 text-white transition-colors hover:bg-white/10"
              >
                <SettingsRecoveryIcon />
              </button>
            </nav>
          </aside>
        </div>
      )}
      <section className="flex min-w-0 flex-1 items-center justify-center bg-primaryGray-16 px-4 py-8">
        <div className="mx-auto flex w-full max-w-screen-sm flex-col items-center text-center">
          <div className="mb-6">
            <OutlineWarningIcon />
          </div>
          <h1 className="mb-3 font-['Lato'] text-h7 font-bold leading-h7 text-primaryGray-1">
            {title}
          </h1>
          <p className="mb-8 font-['Lato'] text-h11 font-regular leading-small text-primaryGray-6">
            {message}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {showRefreshButton && (
              <Button
                buttonType="secondary"
                size="medium"
                onClick={() => window.location.reload()}
                disabled={false}
              >
                Refresh
              </Button>
            )}
            <Button
              buttonType="primary"
              size="medium"
              onClick={handleLogout}
              disabled={false}
            >
              Sign Out
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AuthErrorPage;
